import { assertNever } from "@impero-com/common/utils/obj";

import * as R from "org/risks/types";
import { Guid } from "utils/guid";

import * as T from "./types";

interface EnumMap<E> {
    fieldId: Guid;
    idToEnum: (optionId: Guid) => E;
    enumToId: (fieldEnum: E) => Guid;
}

function buildResponseEnumMap(
    view: R.RiskDirectoryView
): EnumMap<R.ResponseEnum> {
    const {
        content: {
            resource: { structure },
        },
    } = view;
    const {
        blessedFieldIds: { responseId },
        entityLevelFields,
    } = structure;

    const responseField = entityLevelFields.find((f) => f.id === responseId);
    if (responseField === undefined) {
        throw new Error("Couldn't find response metadata");
    }

    if (responseField.fieldType.type !== "Dropdown") {
        throw new Error(
            `Invalid response field ${responseId}: Dropdown expected, got ${responseField.fieldType.type}`
        );
    }

    const { options } = responseField.fieldType;

    const expectedResponseKeys = Object.keys(R.Response).length;
    if (options.length !== expectedResponseKeys) {
        throw new Error(
            `Invalid response field ${responseId}: ${expectedResponseKeys} keys expected, got ${options.length}`
        );
    }

    const optionMap: Record<Guid, R.ResponseEnum> = {};
    options.forEach(
        (option, index) => (optionMap[option.id] = index as R.ResponseEnum)
    );

    return {
        fieldId: responseId,
        enumToId: (response) => options[response].id,
        idToEnum: (responseId) => optionMap[responseId],
    };
}

/**
 * Given a response enum, an inherent impact/likelihood instance and the corresponding residual impact/likelihood instance, returns the correct instance.
 */
function getResidualInstance(
    response: R.ResponseEnum | undefined,
    fallbackInstance: T.DropdownInstance,
    buildInstance: () => T.DropdownInstance,
    buildUndefinedInstance: () => T.DropdownInstance
): T.DropdownInstance {
    switch (response) {
        case undefined:
        case R.Response.Accept:
            return fallbackInstance;
        case R.Response.NotApplicable:
        case R.Response.Avoid:
        case R.Response.Transfer:
            return buildUndefinedInstance();
        case R.Response.Reduce:
            return buildInstance();
        default:
            assertNever(response);
    }
}

function getEnum<E>(instance: T.DropdownInstance, enumMap: EnumMap<E>) {
    const { idToEnum } = enumMap;
    const { value } = instance;
    if (value === undefined) {
        return undefined;
    }
    return idToEnum(value.id);
}

export { type EnumMap, buildResponseEnumMap, getResidualInstance, getEnum };
